import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { FlowBuilderStreamEventReceivedPayload } from '../../types/flowBuilder/flowBuilderApi';
import type {
  FlowEditorTab,
  FlowEditorTabsState,
  FlowEditorTabViewport,
  OpenTabPayload,
} from '../../types/flowEditorTabs';
import { flowBuilderStreamEventReceived } from '../flowBuilder/actions';
export type { FlowEditorTab } from '../../types/flowEditorTabs';

const initialState: FlowEditorTabsState = {
  tabs: [],
  activeTabId: null,
};

function openTabInState(state: FlowEditorTabsState, payload: OpenTabPayload) {
  const { flowId, flowName, flowType, negotiationAgentId, activate = true } = payload;
  const existing = state.tabs.find((tab) => tab.flowId === flowId);
  if (!existing) {
    state.tabs.push({
      flowId,
      flowName,
      flowType,
      negotiationAgentId,
      isDirty: false,
      viewport: { x: 0, y: 0, zoom: 1 },
      openedAt: Date.now(),
    });
  } else {
    if (flowType && !existing.flowType) existing.flowType = flowType;
    if (negotiationAgentId !== undefined) existing.negotiationAgentId = negotiationAgentId;
  }
  if (activate) {
    state.activeTabId = flowId;
  } else if (!state.activeTabId) {
    state.activeTabId = flowId;
  }
}

/**
 * Fluxo que o FlowBuilder gravou no rascunho ganha aba, sem roubar o foco do que o usuário está
 * vendo. Só depois de gravado (`flow_complete`, `sub_flow_created`): em `flow_start` o fluxo novo
 * ainda pode ser reprovado na validação, e a aba apontaria para um fluxo que não existe.
 */
function builderTabToOpen({
  agentId,
  event,
}: FlowBuilderStreamEventReceivedPayload): OpenTabPayload | null {
  if (event.type === 'sub_flow_created') {
    return {
      flowId: event.flowId,
      flowName: event.name,
      flowType: 'auxiliary',
      negotiationAgentId: agentId,
      activate: false,
    };
  }
  if (event.type === 'flow_complete') {
    return {
      flowId: event.flowId,
      flowName: event.name,
      negotiationAgentId: agentId,
      activate: false,
    };
  }
  return null;
}

const flowEditorTabsSlice = createSlice({
  name: 'flowEditorTabs',
  initialState,
  reducers: {
    openTab: (state, action: PayloadAction<OpenTabPayload>) => {
      openTabInState(state, action.payload);
    },

    closeTab: (state, action: PayloadAction<string>) => {
      const flowId = action.payload;
      const index = state.tabs.findIndex((tab) => tab.flowId === flowId);
      if (index === -1) return;
      state.tabs.splice(index, 1);
      if (state.activeTabId === flowId) {
        const nextTab = state.tabs[index] ?? state.tabs[index - 1] ?? null;
        state.activeTabId = nextTab?.flowId ?? null;
      }
    },

    setActiveTab: (state, action: PayloadAction<string>) => {
      const exists = state.tabs.some((tab) => tab.flowId === action.payload);
      if (exists) state.activeTabId = action.payload;
    },

    reorderTabs: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= state.tabs.length ||
        toIndex >= state.tabs.length ||
        fromIndex === toIndex
      ) {
        return;
      }
      const [moved] = state.tabs.splice(fromIndex, 1);
      state.tabs.splice(toIndex, 0, moved);
    },

    setTabDirty: (state, action: PayloadAction<{ flowId: string; isDirty: boolean }>) => {
      const tab = state.tabs.find((t) => t.flowId === action.payload.flowId);
      if (tab) tab.isDirty = action.payload.isDirty;
    },

    setTabViewport: (
      state,
      action: PayloadAction<{ flowId: string; viewport: FlowEditorTabViewport }>,
    ) => {
      const tab = state.tabs.find((t) => t.flowId === action.payload.flowId);
      if (tab) tab.viewport = action.payload.viewport;
    },

    updateTabName: (state, action: PayloadAction<{ flowId: string; flowName: string }>) => {
      const tab = state.tabs.find((t) => t.flowId === action.payload.flowId);
      if (tab) tab.flowName = action.payload.flowName;
    },
    updateTabAgent: (
      state,
      action: PayloadAction<{ flowId: string; negotiationAgentId: string | null }>,
    ) => {
      const tab = state.tabs.find((item) => item.flowId === action.payload.flowId);
      if (tab) tab.negotiationAgentId = action.payload.negotiationAgentId;
    },

    restoreTabs: (
      state,
      action: PayloadAction<{ tabs: FlowEditorTab[]; activeTabId: string | null }>,
    ) => {
      state.tabs = action.payload.tabs;
      const activeExists = action.payload.activeTabId
        ? action.payload.tabs.some((t) => t.flowId === action.payload.activeTabId)
        : false;
      state.activeTabId = activeExists
        ? action.payload.activeTabId
        : (action.payload.tabs[0]?.flowId ?? null);
    },

    resetTabs: () => initialState,
  },
  extraReducers(builder) {
    builder.addCase(flowBuilderStreamEventReceived, (state, action) => {
      const tab = builderTabToOpen(action.payload);
      if (tab) openTabInState(state, tab);
    });
  },
});

export const {
  openTab,
  closeTab,
  setActiveTab,
  reorderTabs,
  setTabDirty,
  setTabViewport,
  updateTabName,
  updateTabAgent,
  restoreTabs,
  resetTabs,
} = flowEditorTabsSlice.actions;

export const flowEditorTabsReducer = flowEditorTabsSlice.reducer;
